import type { JobStatus } from "@loumy/contracts";

// §8/D47 — Live-Status über den API-Gateway-Push (Durable Object → WS; der Job-Worker
// notifiziert die DO bei Status-Wechsel). KEIN Supabase-Realtime browser-facing, und
// REALTIME-ONLY: es gibt KEIN Polling-Fallback mehr. Damit der Stream trotzdem robust ist,
// verbindet der Client bei jedem Abriss automatisch neu (Backoff + Jitter), hält die
// Verbindung per Heartbeat offen und zieht bei jedem (Re)Connect den Zustand EINMAL nach
// (onOpen → verpasste Notices während eines Disconnects), ohne periodisch zu pollen.

export interface StatusNotice {
  jobId: string;
  status: JobStatus;
  phase?: "generate" | "post-process" | null;
  // D147: diskreter Step-Progress (i/n) für den Live-Balken — nur bei reinen Fortschritts-
  // Pushes gesetzt. Ein solcher Push aktualisiert NUR den Balken (kein Cache-Invalidate),
  // nicht den DB-Stand; der Handler unterscheidet ihn an der Präsenz dieser Felder.
  stepIndex?: number;
  stepCount?: number;
}

export interface LiveStatusHandlers {
  /** Pro eingehendem Status-Notice (DO-Broadcast). */
  onNotice: (n: StatusNotice) => void;
  /** Bei jedem (Re)Connect: Sync-on-connect — verpasste Übergänge EINMAL nachziehen (kein Poll). */
  onOpen?: () => void;
}

const HEARTBEAT_MS = 25_000; // Client-Ping hält die WS durch Proxies/Edge-Idle-Timeouts offen.
const MAX_BACKOFF_MS = 30_000;

function liveBaseUrl(): string | null {
  const explicit = import.meta.env.VITE_LIVE_STATUS_URL;
  if (explicit) return explicit.replace(/\/+$/, "");
  if (typeof location === "undefined") return null;
  // Same-origin ableiten (ws/wss je nach http/https) — nur als Dev-Notlösung; in Prod MUSS
  // VITE_LIVE_STATUS_URL auf den live-status-Worker zeigen (Pages hat keinen /connect-Endpunkt).
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
}

/**
 * Abonniert den Live-Status-Stream für den angemeldeten User. `getChannelToken` liefert ein
 * KURZLEBIGES, HMAC-signiertes Channel-Token (F-5/D47: vom api-worker per /v1/live-token mit
 * dem echten JWT gemintet) — das langlebige Supabase-JWT landet NICHT im WS-Query-String.
 * Bei jedem Reconnect wird ein FRISCHES Token gemintet. Liefert sofort eine Unsubscribe-
 * Funktion; danach finden keine weiteren Reconnects/Frames mehr statt.
 */
export function subscribeLiveStatus(getChannelToken: () => Promise<string>, handlers: LiveStatusHandlers): () => void {
  const base = liveBaseUrl();
  if (!base) return () => {};

  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const clearTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed || reconnectTimer) return;
    attempt += 1;
    // Exponentielles Backoff mit Jitter: ~1s, 2s, 4s … HART auf 30s gedeckelt — der Deckel
    // greift NACH dem Jitter (sonst könnte der Jitter-Aufschlag ihn überschreiten).
    const raw = 1000 * 2 ** Math.min(attempt - 1, 5);
    const delay = Math.min(MAX_BACKOFF_MS, Math.round(raw * (0.7 + Math.random() * 0.6)));
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  };

  const connect = async () => {
    if (closed) return;
    let channelToken: string;
    try {
      channelToken = await getChannelToken(); // /v1/live-token (JWT nur im Authorization-Header)
    } catch {
      scheduleReconnect(); // Mint fehlgeschlagen → später erneut (KEIN Polling-Fallback)
      return;
    }
    if (closed) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(`${base}/connect?token=${encodeURIComponent(channelToken)}`);
    } catch {
      scheduleReconnect();
      return;
    }
    socket = ws;

    ws.onopen = () => {
      if (closed) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        return;
      }
      attempt = 0; // erfolgreich verbunden → Backoff zurücksetzen
      heartbeatTimer = setInterval(() => {
        try {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        } catch {
          /* ignore */
        }
      }, HEARTBEAT_MS);
      handlers.onOpen?.(); // Sync-on-connect: verpasste Übergänge EINMAL nachziehen
    };

    ws.onmessage = (ev) => {
      if (closed) return; // nach Teardown eintreffende Frames verwerfen
      try {
        const data = JSON.parse(String(ev.data)) as StatusNotice;
        if (data && typeof data.jobId === "string") handlers.onNotice(data);
      } catch {
        /* nicht-JSON Frame (z.B. Heartbeat-Echo) ignorieren */
      }
    };

    ws.onerror = () => {
      try {
        ws.close(); // erzwingt onclose → genau EIN Reconnect-Pfad
      } catch {
        /* ignore */
      }
    };

    ws.onclose = () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (!closed) scheduleReconnect();
    };
  };

  // Realtime-only: nach Netz-/Tab-Resume SOFORT reconnecten, statt auf den nächsten Backoff-
  // Timer zu warten (es gibt kein Polling-Sicherheitsnetz). Greift nur, wenn gerade KEINE
  // Verbindung steht/aufgebaut wird; der anschließende onOpen-Sync holt verpasste Übergänge auf.
  const reconnectNow = () => {
    if (closed) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    attempt = 0;
    void connect();
  };
  if (typeof window !== "undefined") window.addEventListener("online", reconnectNow);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", reconnectNow);

  void connect();

  return () => {
    closed = true;
    if (typeof window !== "undefined") window.removeEventListener("online", reconnectNow);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", reconnectNow);
    clearTimers();
    try {
      socket?.close();
    } catch {
      /* ignore */
    }
  };
}
